import React, { useCallback, useMemo } from 'react';

import { Box, Header, Icon, Text } from '@rbilabs/universal-components';
import { get } from 'lodash-es';

import Link from 'components/link';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { getDesignToken, primitive } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { region } from 'utils/environment';
import { isWeb } from 'utils/environment';
import { Region } from 'utils/environment/types';
import { isExternalLink } from 'utils/is-external-link';

import { CCPAButtonLink, CCPA_COMPLIANCE } from './ccpa-button-link';

interface SingleNode {
  title: string;
  path: string;
}
interface ArrayNode {
  name: { locale: string };
  pages: [SingleNode];
}
interface IListProps {
  title: string;
  entries: [SingleNode | ArrayNode];
  borderColor?: string;
  sectionHeadings?: boolean;
  onDismiss?: () => void;
  testID?: string;
}

const addPathSlash = (path: string): string =>
  isExternalLink(path) || path.startsWith('/') ? path : `/${path}`;

const getPaths = (nodes: [SingleNode | ArrayNode]): any => {
  return nodes.map((node: SingleNode | ArrayNode) => {
    const title = get(node, 'localeTitle.locale') || get(node, 'title') || '';
    const path = get(node, 'localePath.locale.current') || get(node, 'path.current') || '';
    if ((node as SingleNode).path) {
      return {
        title,
        // Fix relative paths not having a leading slash
        path: addPathSlash(get(node, 'redirectUrl.locale') || path),
      };
    }
    return {
      title: (node as ArrayNode).name?.locale,
      pages: getPaths((node as ArrayNode).pages),
    };
  });
};

const SectionHeader = Header.withConfig({
  variant: 'headerThree',
  textTransform: getDesignToken('text-transform-headlines'),
  fontSize: 'xl',
  marginTop: '$4',
  marginBottom: '$0',
  paddingBottom: '$4',
  borderBottomWidth: 1,
  borderBottomColor: primitive.$greyFour,
});

const SectionGroup = Box.withConfig<{ hasHeader?: boolean }>(p => ({
  paddingTop: p.hasHeader ? '0' : '4',
  paddingBottom: '4',
}));

const SectionItem = Box.withConfig({
  paddingTop: '$2',
  paddingBottom: '$4',
  borderBottomWidth: 1,
  borderBottomColor: primitive.$greyFour,
});

export default function ListOfLinks({
  onDismiss,
  entries,
  sectionHeadings,
  borderColor,
}: IListProps) {
  const filterMenuPaths = useCallback((entries: any) => {
    return entries.filter((entry: SingleNode | ArrayNode) => {
      if ('pages' in entry) {
        return filterMenuPaths(entry.pages);
      }
      return true;
    });
  }, []);

  const paths = useMemo(() => filterMenuPaths(getPaths(entries)), [entries, filterMenuPaths]);

  const onPress = useCallback(
    (node: SingleNode) => {
      logEvent(CustomEventNames.BUTTON_CLICK_STATIC_PAGES, EventTypes.Navigation, {
        Name: node.title,
      });

      onDismiss?.();
    },
    [onDismiss]
  );

  return (
    <Box>
      {paths.map((entry: any, i: number) => {
        const isSection = Array.isArray(entry.pages);
        const pages = isSection ? entry.pages : [entry];
        const showHeader = isSection && sectionHeadings;

        return (
          <Box key={`path-group-${i}`}>
            {showHeader && (
              <SectionHeader
                focusable
                accessible
                accessibilityRole="header"
                accessibilityLabel={entry.title}
              >
                {entry.title}
              </SectionHeader>
            )}

            <SectionGroup hasHeader={showHeader}>
              {pages.map((node: SingleNode) => {
                const isCCPALink: boolean = !!node.path?.includes(CCPA_COMPLIANCE);
                const showCCPAButtonLink = isCCPALink && region === Region.US && isWeb;

                return (
                  <SectionItem marginTop="2" key={`${node.title}: ${node.path}`}>
                    {showCCPAButtonLink ? (
                      <CCPAButtonLink />
                    ) : (
                      <Link
                        to={node.path}
                        onPress={() => onPress(node)}
                        isExternal={isExternalLink(node.path)}
                        justifyContent={'space-between'}
                        _text={{
                          fontSize: 'md',
                        }}
                      >
                        <Text>{node.title}</Text>
                        <Icon
                          variant="next"
                          ml="$1"
                          width="4"
                          {...hiddenAccessibilityPlatformProps}
                        />
                      </Link>
                    )}
                  </SectionItem>
                );
              })}
            </SectionGroup>
          </Box>
        );
      })}
    </Box>
  );
}
